import _ from "lodash";
import { useEffect, useState } from "react";
import { useLoaderData, useRevalidator } from "react-router-dom";

import { inject } from "../../../../../lib/itn-ui-kit/services/service-container";
import useService from "../../../../hooks/useService";
import { getMenuList } from "../config/helper";

import useContactsHandlers from "./useContactHandlers";
import { useContactStore } from "./useContactStore";

export default function useContactsForForms({ data, questionKey }) {
  const currentUserService = inject("current-user-service");
  const userPermissions = currentUserService?.userRoles?.permissions || [];
  const permissions = {
    ADD: userPermissions.includes("core.add_contact"),
    EDIT: userPermissions.includes("core.change_contact"),
    DELETE: userPermissions.includes("core.delete_contact"),
  };

  const contactRepository = useService("repositories/contact-repository");
  const followUpFormRepository = useService("repositories/follow-ups-form-repository");
  const { caseId, fieldOptions, followUpId, formId } = useLoaderData();
  const search = useContactStore((state) => state.search);
  const { revalidate } = useRevalidator();

  const [isLoading, setIsLoading] = useState(false);
  const [visibleData, setVisibleData] = useState([]);

  const handlers = useContactsHandlers({
    id: caseId,
    contactRepository,
    revalidate,
  });

  const menuList = getMenuList(fieldOptions, handlers.openAddModal);

  const convertKeysToCamelCase = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(convertKeysToCamelCase);
    }
    if (obj && typeof obj === "object") {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [_.camelCase(key), convertKeysToCamelCase(value)])
      );
    }
    return obj;
  };

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    (async () => {
      try {
        const queryParams = {
          ...(search?.trim() ? { search } : {}),
        };
        const items = await followUpFormRepository.query({
          urlPath: `actionable_items/${followUpId}/forms/${formId}/question/${questionKey}`,
          params: Object.keys(queryParams).length ? queryParams : null,
        });
        if (!mounted) {
          return;
        }
        setVisibleData(items?.readOnly?.records || []);
      } catch (error) {
        handlers?.handleFailure?.(error);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [data, search, followUpId, formId, questionKey, followUpFormRepository]);

  return {
    permissions,
    menuList,
    visibleData: convertKeysToCamelCase(visibleData),
    handlers,
    isLoading,
  };
}
